// src/lib/managers/conversation/facts.ts

/**
 * Les faits retenus dans la conversation (contrat par étapes ; Api-Ai :
 * docs/refonte-assistant/CONCEPTION-memoire-faits.md) : une courte liste « clé = valeur », le plus
 * récent en dernier. L'app la garde, l'envoie avec chaque appel du tour, et la remplace par celle que
 * rend la fin du tour ou le compactage.
 *
 * L'app y écrit elle-même le contact que l'utilisateur choisit à la question « Lequel ? », et en
 * retire un fait quand il le demande à l'écran. Les mêmes règles que l'Api-Ai (`etapes/faits.py`) :
 * une clé sur une ligne, unique sans tenir compte des majuscules ni des accents ; une liste pleine
 * perd son fait le plus ancien.
 */

import { clip, oneLine } from "./contractText";

/** Un fait retenu, tel qu'il voyage dans le contrat. */
export type ConversationFact = { cle: string; valeur: string };

/** Bornes du contrat (les mêmes que l'Api-Ai, `etapes/contrat.py`). */
export const FACTS_MAX = 15;
const KEY_MAX = 40;
const VALUE_MAX = 100;

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Une clé, sans tenir compte des majuscules, des accents ni des espaces en trop. */
const normalizeKey = (key: string): string =>
    key
        .normalize("NFKD")
        .replace(/\p{M}/gu, "")
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .join(" ");

/**
 * La liste avec ce fait, le plus récent en dernier : une clé déjà retenue est remplacée ; pleine, la
 * liste perd son fait le plus ancien. Une clé ou une valeur hors bornes : la liste telle quelle.
 */
export function noteFact(
    facts: readonly ConversationFact[],
    key: string,
    value: string,
): ConversationFact[] {
    const cle = oneLine(key, KEY_MAX);
    const valeur = oneLine(value, VALUE_MAX);
    if (!cle || !valeur) return [...facts];
    return [...withoutFact(facts, cle), { cle, valeur }].slice(-FACTS_MAX);
}

/** La liste sans le fait de cette clé. */
export function withoutFact(
    facts: readonly ConversationFact[],
    key: string,
): ConversationFact[] {
    const wanted = normalizeKey(key);
    return facts.filter((fact) => normalizeKey(fact.cle) !== wanted);
}

/**
 * Les faits rendus par l'Api-Ai ou l'Api-Backend, lus sans confiance : un fait illisible ou hors
 * bornes est ignoré ; deux fois la même clé, le dernier gagne. Ce qui n'est pas une liste (champ
 * absent : un serveur qui ne connaît pas les faits) : `undefined`, l'app garde les siens.
 */
export function readFacts(raw: unknown): ConversationFact[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    let facts: ConversationFact[] = [];
    for (const item of raw) {
        if (typeof item !== "object" || item === null) continue;
        const { cle, valeur } = item as Record<string, unknown>;
        if (typeof cle === "string" && typeof valeur === "string") {
            facts = noteFact(facts, cle, valeur);
        }
    }
    return facts;
}

/**
 * La clé du fait d'un contact choisi : le nom demandé, coupé à la borne d'une clé s'il est plus long.
 * `contactOf` coupe de la même façon : un nom long retrouve son contact.
 */
export const contactKey = (name: string): string => clip(name, KEY_MAX);

/**
 * Le fait d'un contact choisi pour ce nom, sauf si son adresse est déjà retenue, sous ce nom ou un
 * autre : une adresse, un seul fait (CONCEPTION-personnes-de-la-conversation de l'Api-Ai, R2).
 */
export function noteContact(
    facts: readonly ConversationFact[],
    name: string,
    email: string,
): ConversationFact[] {
    return chosenContacts(facts).has(email.toLowerCase())
        ? [...facts]
        : noteFact(facts, contactKey(name), email);
}

/** Les adresses déjà choisies dans la conversation : les faits dont la valeur est une adresse, en minuscules. */
export const chosenContacts = (
    facts: readonly ConversationFact[],
): Set<string> =>
    new Set(
        facts
            .filter((fact) => EMAIL.test(fact.valeur))
            .map((fact) => fact.valeur.toLowerCase()),
    );

/**
 * L'adresse retenue pour ce nom : le fait dont la clé est ce nom, si sa valeur est une adresse mail
 * et rien d'autre. Sinon `undefined` : le nom se cherche dans les contacts, comme d'habitude.
 */
export function contactOf(
    facts: readonly ConversationFact[],
    name: string,
): string | undefined {
    const wanted = normalizeKey(contactKey(name));
    const fact = facts.find(
        (candidate) => normalizeKey(candidate.cle) === wanted,
    );
    return fact && EMAIL.test(fact.valeur) ? fact.valeur : undefined;
}

/**
 * Les faits courants, sans ceux que le compactage a retirés : ceux de `before` (la liste envoyée au
 * compactage) absents de `after` (la liste qu'il rend). Ce qui a changé pendant le compactage (un fait
 * oublié à l'écran, un contact choisi) est gardé tel quel : le compactage ne fait que retirer.
 */
export function withoutRemoved(
    current: readonly ConversationFact[],
    before: readonly ConversationFact[],
    after: readonly ConversationFact[],
): ConversationFact[] {
    const removed = before.filter(
        (fact) => withoutFact(after, fact.cle).length === after.length,
    );
    return removed.reduce<ConversationFact[]>(
        (facts, fact) => withoutFact(facts, fact.cle),
        [...current],
    );
}

export const sameFacts = (
    a: readonly ConversationFact[],
    b: readonly ConversationFact[],
): boolean =>
    a.length === b.length &&
    a.every((fact, i) => fact.cle === b[i].cle && fact.valeur === b[i].valeur);
